import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Camera, Mail, CheckCircle2 } from 'lucide-react';
import api from '@/lib/api';
import LanguageSwitcher from '@/components/LanguageSwitcher';

export default function PhotographerLogin() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/photographer/auth/request-magic-link', { email: email.trim().toLowerCase() });
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 flex items-center justify-center p-4">
      <div className="absolute top-4 right-4">
        <LanguageSwitcher />
      </div>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl mb-4 shadow-lg">
            <Camera className="w-8 h-8 text-white" />
          </Link>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">{t('photographer.loginTitle')}</h1>
          <p className="text-slate-600 text-sm">{t('photographer.loginSubtitle')}</p>
        </div>

        <Card className="p-8">
          {sent ? (
            <div className="text-center space-y-4">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-100 text-emerald-700 mx-auto">
                <CheckCircle2 className="w-7 h-7" />
              </div>
              <h2 className="text-xl font-semibold text-slate-900">{t('photographer.linkSent')}</h2>
              <p className="text-slate-600 text-sm">{t('photographer.linkSentBody', { email })}</p>
              <Button variant="ghost" onClick={() => { setSent(false); setEmail(''); }}>
                {t('photographer.tryAnother')}
              </Button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label>{t('auth.email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="email" required value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('photographer.emailPlaceholder')}
                    className="pl-11 h-12"
                  />
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full h-12 bg-gradient-to-r from-emerald-500 to-teal-600 text-white">
                {loading ? t('common.loading') : t('photographer.sendLink')}
              </Button>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
}
